import { Select } from '@/components/selects';
import { ISelectItem } from '@/models/select';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import { Audience } from '@/models/audiences';
import { Loadable } from '@/components/loaders';
import {
  CyclrActionParameter,
  CyclrActionTriggerValue,
  DistributionChannelConnector,
  DistributionChannelContent,
  DistributionChannelParameterUpdate,
} from '@/models/distributionChannels';
import { useCreateDistributionChannelMutation, useDistributionChannelAuthenticatedMutation } from '@/services/endpoints/distributionChannels';
import { useWorkspace } from '@/features/workspaces/hooks';
import { CampaignDestinationItem } from './types';
import { useCyclrTemplatesQuery } from '@/services/endpoints/integrations';
import RouteConstants from '@/features/router/RouteConstants';
import { popupCenter } from '@/utils';
import { Input } from '@/lib';
import { ConnectorInfo } from './ConnectorInfo';

type DestinationItemSetupProps = {
  setDistributionChannelContent: (connector: DistributionChannelConnector, content: DistributionChannelContent) => void;
  parameterValueChange: (connector: DistributionChannelConnector, update: DistributionChannelParameterUpdate) => void;
  setIsDistributionChannelLoading: (loading: boolean) => void;
  audience: Audience;
  data: CampaignDestinationItem;
};

export const DestinationItemSetup: FC<DestinationItemSetupProps> = ({
  setDistributionChannelContent,
  parameterValueChange,
  setIsDistributionChannelLoading,
  audience,
  data,
}) => {
  const { t } = useTranslation('destinations');
  const workspace = useWorkspace();

  const { connector, distributionChannelContent: defaultDistributionChannel } = data;

  const { data: templates, isLoading: templateIsLoading } = useCyclrTemplatesQuery({
    workspaceId: workspace.id,
  });

  const selectedTemplate = useMemo(() => templates?.find((t) => t.Connectors.find((c) => c.Name === connector.name)), [templates, connector]);

  const [createDistributionChannel, { isLoading: isCreateLoading, data: createData }] = useCreateDistributionChannelMutation();

  const [distributionChannelAuthenticated, { isLoading: isGetLoading, data: getData }] = useDistributionChannelAuthenticatedMutation();

  const [status, setStatus] = useState<string>(defaultDistributionChannel?.integrationsResult?.cyclr?.status || 'CREATE');
  const [distributionChannel, setDistributionChannel] = useState<DistributionChannelContent | undefined>(defaultDistributionChannel);
  const [triggerParam, setTriggerParam] = useState<{ [paramId: string]: CyclrActionTriggerValue | undefined }>({});

  useEffect(
    () => setIsDistributionChannelLoading(templateIsLoading || isGetLoading || isCreateLoading),
    [setIsDistributionChannelLoading, templateIsLoading, isGetLoading, isCreateLoading]
  );

  useEffect(() => {
    const tempDistributionChannel = getData || createData || distributionChannel;
    setDistributionChannel(tempDistributionChannel);
    setStatus(tempDistributionChannel?.integrationsResult.cyclr.status || status);
    if (tempDistributionChannel?.integrationsResult.cyclr.status === 'AUTHENTICATION_REQUIRED') {
      const action = tempDistributionChannel.integrationsResult.cyclr.action;
      authenticationScreen(
        `${action.authUrl}?id=${action.accountConnectors[0]}&token=${action.token}&targetOrigin=${location.origin + RouteConstants.cyclrAuthRedirect}`,
        tempDistributionChannel
      );
    } else if (tempDistributionChannel?.integrationsResult.cyclr.status === 'ACTIVABLE') {
      setDistributionChannelContent(connector, tempDistributionChannel);
    } else {
      setDistributionChannelContent(connector, tempDistributionChannel);
    }
  }, [createData, getData]);

  const authenticationScreen = (url: string, distributionChannel: DistributionChannelContent) => {
    (window as any).cyclrAuthenticationCompleted = (success: boolean, w: Window) => {
      w.close.apply(w);
      delete (window as any).cyclrAuthenticationCompleted;
      distributionChannelAuthenticated({
        workspaceId: workspace.id,
        audienceId: audience?.id,
        distributionChannelId: distributionChannel?.distributionChannel.id as any,
      });
    };
    const newWindow = popupCenter({
      url,
      title: t('authenticate'),
      w: 640,
      h: 640,
    });
    (window as any).authWindow = newWindow;
  };

  // TODO: selected template is empty
  const saveDestination = () => {
    createDistributionChannel({
      workspaceId: workspace.id,
      audienceId: audience?.id as string,
      configData: {
        templateId: selectedTemplate?.Id,
        templateName: selectedTemplate?.Name,
      },
    });
  };

  const parameterTemplate = (param: CyclrActionParameter, stepIds?: string[], index = 1) => {
    const translationKey = distributionChannel?.distributionChannel?.name.toLowerCase().replace(/\s+/g, '_');
    const { name, triggerParameterLookupValues } = param;
    const paramName = (triggerParameterLookupValues?.trigger?.Name || name).toLowerCase().replace(/\s+/g, '_');

    return (
      <Fragment key={param.parameterId}>
        <div className={'flex h-auto flex-col gap-2 text-left font-medium text-gray-800'}>
          {param.mappingType !== null ? (
            <Select
              placeholder={t(`edit.cyclr.${translationKey}.${paramName}` as any)}
              items={param.triggerParameterLookupValues?.values.map<ISelectItem>((lookupValue) => ({
                ...lookupValue,
                id: lookupValue?.name,
                label: lookupValue?.name,
              }))}
              className='w-full'
              value={
                data.selectedParameters[param.parameterId]
                  ? {
                      id: data.selectedParameters[param.parameterId]?.name,
                      label: data.selectedParameters[param.parameterId]?.name,
                    }
                  : undefined
              }
              onChange={(value: ISelectItem) => {
                const lookupValue = value as ISelectItem & CyclrActionTriggerValue;
                if (lookupValue.nextTrigger) {
                  setTriggerParam({
                    ...triggerParam,
                    [index]: lookupValue,
                  });
                } else {
                  parameterValueChange(connector, {
                    isAccountConnectorParameter: param.isAccountConnectorParameter,
                    accountConnectorId: param.accountConnectorId,
                    propertyId: param.propertyId,
                    stepIds: stepIds || param.stepIds,
                    parameterId: param.parameterId,
                    mappingType: param.mappingType,
                    value: lookupValue.value,
                    name: lookupValue.name,
                  });
                }
              }}
            />
          ) : (
            <Input
              className={'w-full'}
              defaultValue={distributionChannel.distributionChannel.destination?.split(',')[index - 1] || ''}
              placeholder={t(`edit.cyclr.${translationKey}.${paramName}` as any)}
              onChange={(e) => {
                parameterValueChange(connector, {
                  isAccountConnectorParameter: param.isAccountConnectorParameter,
                  accountConnectorId: param.accountConnectorId,
                  propertyId: param.propertyId,
                  stepIds: param.stepIds,
                  parameterId: param.parameterId,
                  mappingType: 'StaticValue',
                  value: e.target.value,
                  name: null,
                });
              }}
            />
          )}
        </div>

        {triggerParam[index]?.nextTrigger &&
          parameterTemplate(
            {
              triggerParameterLookupValues: {
                values: triggerParam[index]?.nextTrigger?.values,
              },
              name: triggerParam[index]?.nextTrigger?.trigger.Name,
              description: '',
              parameterId: param.parameterId,
              mappingType: param.mappingType,
            } as any,
            stepIds || param.stepIds,
            index + 1
          )}
      </Fragment>
    );
  };

  const statusTemplate = () => {
    switch (status) {
      case 'PARAMETERS_REQUIRED':
        return (
          <div className='flex flex-col gap-4'>
            {distributionChannel?.integrationsResult.cyclr.action.parameters.filter((param) => !param.isOptional).map((param) => parameterTemplate(param))}
          </div>
        );
      default:
        return <>Error</>;
    }
  };

  useEffect(() => {
    if (!selectedTemplate) {
      return;
    }

    if (status === 'CREATE') {
      saveDestination();
    }
  }, [status, selectedTemplate]);

  return (
    <div className='grid grid-cols-[540px_1fr] py-10'>
      <ConnectorInfo connector={connector} />
      <Loadable isLoading={templateIsLoading || isCreateLoading || isGetLoading}>{statusTemplate()}</Loadable>
    </div>
  );
};
